import { Box, BoxProps, SvgIconProps, Typography } from '@mui/material';
import { memo, useEffect, useRef, useState, type FC } from 'react';

type AnimationPhase = 'PULSE' | 'BIG' | 'SMALL' | 'SMALL_ANIMATE';

export type OverlayIcon = FC<
  SvgIconProps & {
    canAnimate: boolean;
    moveDirection?: 'up' | 'down';
    position?: number;
  }
>;

export type OverlayState = {
  id: number;
  label: JSX.Element;
  icon: OverlayIcon;
  iconColor: string;
  iconContrastColor: string;
};

export interface GateFigureProps {
  boxProps?: BoxProps;
  state?: OverlayState;
  position?: number;
  moveDirection?: 'up' | 'down';
}

type GeometryStyle = {
  height: number | string;
  width: number | string;
  top: number | string;
  right: number | string;
  iconSize: number;
  transition: string;
};

const PHASE_MAP: Record<AnimationPhase, GeometryStyle> = {
  PULSE: {
    height: 180,
    width: 180,
    top: '50%',
    right: '50%',
    iconSize: 140,
    transition: 'all 0.3s ease-in-out',
  },
  BIG: {
    height: 160,
    width: 160,
    top: '50%',
    right: '50%',
    iconSize: 128,
    transition: 'all 0.5s ease-in-out',
  },
  SMALL: {
    height: 48,
    width: 48,
    top: '50%',
    right: '10%',
    iconSize: 30,
    transition: `
      width 1.2s ease-in-out,
      height 1.2s ease-in-out,
      top 1.2s ease-in-out,
      right 1.2s ease-in-out,
      font-size 1.2s ease-in-out 
    `,
  },
  SMALL_ANIMATE: {
    height: 48,
    width: 48,
    top: '50%',
    right: '10%',
    iconSize: 30,
    transition: `
      width 1.2s ease-in-out,
      height 1.2s ease-in-out,
      top 1.2s ease-in-out,
      right 1.2s ease-in-out,
      font-size 1.2s ease-in-out 
    `,
  },
};

export const GateFigure: FC<GateFigureProps> = ({ boxProps, state, moveDirection, position }) => {
  const [phase, setPhase] = useState<AnimationPhase>('PULSE');

  return (
    <Box position="relative" {...boxProps}>
      <Box
        component="img"
        src="/static/gate-figure.png"
        sx={{
          width: '100%',
          opacity: phase === 'SMALL' || phase === 'SMALL_ANIMATE' || !state ? 1 : 0.3,
          transition: 'opacity 0.5s ease-in-out',
        }}
      />

      {state && (
        <Overlay
          {...{
            phase,
            setPhase,
            state,
            moveDirection,
            position,
          }}
        />
      )}
    </Box>
  );
};

const Overlay = memo(
  ({
    state,
    phase,
    setPhase,
    position,
    moveDirection,
  }: {
    phase: AnimationPhase;
    setPhase: (phase: AnimationPhase) => void;
    state: OverlayState;
    moveDirection: GateFigureProps['moveDirection'];
    position: GateFigureProps['position'];
  }) => {
    const lastStateRef = useRef<number | null>(null);

    const {
      icon: IconComponent,
      label,
      iconColor: stateIconColor,
      iconContrastColor,
    } = state ?? {};

    const currentGeo = PHASE_MAP[phase];

    useEffect(() => {
      lastStateRef.current = null;
    }, []);

    useEffect(() => {
      let time = 0;
      const delta = 200;
      let currentPhase: AnimationPhase = 'PULSE';

      const interval = setInterval(() => {
        time += delta;

        if (time > 6500) {
          if (currentPhase !== 'SMALL_ANIMATE') {
            currentPhase = 'SMALL_ANIMATE';
            setPhase(currentPhase);
          }
        } else if (time > 2500) {
          if (currentPhase !== 'SMALL') {
            currentPhase = 'SMALL';
            setPhase(currentPhase);
          }
        } else if (time > 500) {
          if (currentPhase !== 'BIG') {
            currentPhase = 'BIG';
            setPhase(currentPhase);
          }
        }
      }, delta);

      return () => {
        clearInterval(interval);
        setPhase('PULSE');
      };
    }, [state.id]);

    const showIconBackground = phase === 'SMALL' || phase === 'SMALL_ANIMATE';

    const bgTransition = showIconBackground
      ? 'opacity 0.3s ease-in-out 1.5s'
      : 'opacity 0.1s ease-in-out';

    const iconColor =
      phase === 'BIG' ? 'white' : showIconBackground ? iconContrastColor : stateIconColor;
    const iconBgColor = stateIconColor;

    const colorTransition = showIconBackground
      ? 'color 0.3s ease-in-out 1.5s'
      : 'color 0.1s ease-in-out';

    return (
      <>
        <Box
          sx={{
            position: 'absolute',
            transform: 'translate(50%, -50%)',
            display: 'grid',
            placeItems: 'center',
            top: currentGeo.top,
            right: currentGeo.right,
            height: currentGeo.height,
            width: currentGeo.width,
            transition: currentGeo.transition,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: iconBgColor,
              opacity: showIconBackground ? 1 : 0,
              transition: bgTransition,
              zIndex: 0,
              borderRadius: '50%',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              color: iconColor,
              transition: colorTransition,
              display: 'flex',
            }}
          >
            {IconComponent && (
              <IconComponent
                canAnimate={phase === 'SMALL_ANIMATE'}
                position={position}
                moveDirection={moveDirection}
                sx={{
                  fontSize: currentGeo.iconSize,
                  transition: currentGeo.transition,
                }}
              />
            )}
          </Box>
        </Box>
        <Typography
          mt={1}
          fontWeight="bold"
          textAlign="center"
          fontSize="small"
          sx={{
            width: '100%',
            opacity: phase === 'PULSE' || phase === 'BIG' ? 1 : 0,
            position: 'absolute',
            top: 190,
            transition: 'all 0.3s ease-in-out',
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
      </>
    );
  },
);
